import { useSyncExternalStore } from 'react'

/**
 * Library → Mine's «Колоды | Тексты» choice, remembered per device (handoff §7).
 *
 * Not part of Mine's P4 kept state (folder, sort, page, search: ADR-106 Q11),
 * which belongs to the Decks segment and is per session. This is a device
 * preference, so it survives a reload and a new session.
 *
 * Read by everything that draws Mine, so arriving there by swipe or by tap
 * shows the same segment (ADR-106 Decision 8), and by the snapshot neighbour's
 * validity key (ADR-106 fourth block). The switch is never part of the URL:
 * it is not a stop, and a swipe never changes it (ADR-115 Decision 11).
 */
export type MineSegment = 'decks' | 'texts'

const KEY = 'cs-mine-segment'
const listeners = new Set<() => void>()
// The value in force for this page load, even when storage is unavailable
// (a private window, blocked site data): the choice then lasts until reload.
let current: MineSegment | null = null

function readStored(): MineSegment {
  try {
    return window.localStorage.getItem(KEY) === 'texts' ? 'texts' : 'decks'
  } catch {
    return 'decks'
  }
}

export function readMineSegment(): MineSegment {
  if (current === null) current = readStored()
  return current
}

export function writeMineSegment(segment: MineSegment): void {
  current = segment
  try {
    window.localStorage.setItem(KEY, segment)
  } catch {
    // Storage refused: kept in memory for this page load only.
  }
  listeners.forEach((l) => l())
}

/** Tests only: forget the in-memory value so the next read goes to storage. */
export function resetMineSegmentForTests(): void {
  current = null
}

function subscribe(onChange: () => void): () => void {
  listeners.add(onChange)
  return () => { listeners.delete(onChange) }
}

export function useMineSegment(): [MineSegment, (segment: MineSegment) => void] {
  const segment = useSyncExternalStore(subscribe, readMineSegment, () => 'decks' as MineSegment)
  return [segment, writeMineSegment]
}
